import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Home from "./pages/Home";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ProgramDetail from "./pages/ProgramDetail";
import ServiceDetail from "./pages/ServiceDetail";
import Checkout from "./pages/Checkout";
import PaymentSuccess from "./pages/PaymentSuccess";
import Assessment from "./pages/Assessment";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import AuthCallback from "./pages/AuthCallback";

const AssessmentQuiz = lazy(() => import("./pages/AssessmentQuiz"));
const AssessmentResults = lazy(() => import("./pages/AssessmentResults"));
const SharedResults = lazy(() => import("./pages/SharedResults"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Account = lazy(() => import("./pages/Account"));
const PranicHealing = lazy(() => import("./pages/PranicHealing"));

const PageFallback = () => (
  <div
    style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "rgba(255,255,255,0.5)",
      fontSize: "0.95rem",
      letterSpacing: "0.2em",
      textTransform: "uppercase",
    }}
    data-testid="route-loading"
  >
    Loading…
  </div>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Intercept the OAuth redirect (`#session_id=...`) BEFORE normal routing so the
// session exchange fires reliably, then hand back to <Routes>.
const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/program/:serviceId" element={<ServiceDetail />} />
        <Route path="/pricing/:programId" element={<ProgramDetail />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/assessment" element={<Assessment />} />
        <Route path="/assessment/start" element={<AssessmentQuiz />} />
        <Route path="/assessment/results" element={<AssessmentResults />} />
        <Route path="/results/:token" element={<SharedResults />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/account" element={<Account />} />
        <Route path="/services/pranic-healing" element={<PranicHealing />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </BrowserRouter>
        <Toaster />
      </div>
    </HelmetProvider>
  );
}

export default App;
